import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Mail, Loader2 } from "lucide-react";
import { useAuth } from "../../lib/AuthContext";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../ui/button";

export default function EmailChangeSection() {
  const { t } = useTranslation("common");
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const T = (k, o) => t(`settings.profile.${k}`, o);

  const startChange = () => {
    setError("");
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/me/email/change-request", {
        new_email: newEmail.trim(),
        current_password: password,
        totp_code: user?.totp_enabled ? totpCode.trim() : undefined,
      });
      setSuccess(true);
      setOpen(false);
      setNewEmail("");
      setPassword("");
      setTotpCode("");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || T("error_fallback"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="border border-zinc-200 rounded-2xl bg-white p-6 mb-6" data-testid="email-change-section">
      <div className="flex items-center gap-3 mb-4">
        <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-zinc-100 text-zinc-700">
          <Mail size={18} strokeWidth={1.75} />
        </span>
        <div>
          <h2 className="font-display text-lg font-semibold text-zinc-950">{T("title")}</h2>
          <p className="text-xs text-zinc-500 mt-0.5 max-w-md">{T("subtitle")}</p>
        </div>
      </div>

      {success && (
        <div
          className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 mb-4"
          data-testid="email-change-success"
        >
          <p className="text-sm font-medium text-emerald-900">{T("success_title")}</p>
          <p className="text-xs text-emerald-700 mt-1">{T("success_body")}</p>
        </div>
      )}

      <p className="text-xs text-zinc-500 mb-1">{T("current_email_label")}</p>
      <p className="text-sm text-zinc-800 mb-4" data-testid="email-change-current">{user?.email}</p>

      {error && (
        <div
          role="alert"
          aria-live="assertive"
          className="rounded-lg border border-rose-200 bg-rose-50 text-rose-700 text-sm px-3 py-2 mb-4"
          data-testid="email-change-error"
        >
          {error}
        </div>
      )}

      {!open ? (
        <Button
          onClick={startChange}
          data-testid="email-change-open"
          className="h-10 bg-zinc-950 hover:bg-zinc-800 text-white rounded-lg"
        >
          {T("change_cta")}
        </Button>
      ) : (
        <form onSubmit={submit} className="space-y-3 max-w-xs">
          <div>
            <label className="text-xs text-zinc-500 block mb-1">{T("new_email_label")}</label>
            <input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              data-testid="email-change-new"
              className="w-full h-10 px-3 rounded-lg border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-300"
              required
            />
          </div>
          <div>
            <label className="text-xs text-zinc-500 block mb-1">{T("password_label")}</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="email-change-password"
              className="w-full h-10 px-3 rounded-lg border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-300"
              required
            />
          </div>
          {user?.totp_enabled && (
            <div>
              <label className="text-xs text-zinc-500 block mb-1">{T("totp_label")}</label>
              <input
                type="text"
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value)}
                data-testid="email-change-totp"
                className="w-full h-10 px-3 rounded-lg border border-zinc-200 text-sm tracking-[0.2em] focus:outline-none focus:ring-2 focus:ring-zinc-300"
                required
              />
            </div>
          )}
          <Button
            type="submit"
            disabled={busy || !newEmail || !password}
            data-testid="email-change-submit"
            className="h-10 bg-zinc-950 hover:bg-zinc-800 text-white rounded-lg"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : T("submit")}
          </Button>
        </form>
      )}
    </section>
  );
}
